import { useState } from "react";
import {
    Button,
    Image as AntdImage,
    Slider,
    message,
    Space,
    Typography,
    Flex,
    Card,
} from "antd";
import GeneralUploader from "../pdfEditor/Uploader";
import { downloadImage } from "../pdfEditor/utils";

const { Title, Text } = Typography;

const canvasToBlob = (canvas, type = "image/jpeg", quality = 0.8) =>
    new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
            type,
            quality
        );
    });

const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

const waitForImageLoad = (img) =>
    new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Image failed to load"));
    });

export default function ImageCompress() {
    const [file, setFile] = useState(null);
    const [fileUrl, setFileUrl] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [qualityPercent, setQualityPercent] = useState(80);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processedDataUrl, setProcessedDataUrl] = useState(null);
    const [outName, setOutName] = useState("");

    const clearObjectUrl = () => {
        if (fileUrl) {
            URL.revokeObjectURL(fileUrl);
            setFileUrl(null);
        }
    };

    const handleUploadChange = (info) => {
        const uploaded = Array.isArray(info) ? info[0] : info?.file ?? info;
        if (!uploaded) return;

        const actual = uploaded.originFileObj ?? uploaded;
        clearObjectUrl();

        const objectUrl = URL.createObjectURL(actual);
        setFile(actual);
        setFileUrl(objectUrl);
        setImageSrc(objectUrl);
        setProcessedDataUrl(null);
        setOutName("");
    };

    const handleCompress = async () => {
        if (!imageSrc || !file)
            return message.error("Please upload an image first");

        setIsProcessing(true);
        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = imageSrc;
            await waitForImageLoad(img);

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            const quality = Math.max(0.01, Math.min(1, qualityPercent / 100));
            const blob = await canvasToBlob(canvas, "image/jpeg", quality);
            const dataUrl = await blobToDataUrl(blob);

            clearObjectUrl();
            setProcessedDataUrl(dataUrl);

            const baseName = file.name.replace(/\.[^/.]+$/, "");
            setOutName(`${baseName}_q${Math.round(qualityPercent)}.jpg`);
            message.success("Image compressed!");
        } catch (err) {
            console.error(err);
            message.error("Compression failed");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDownload = () => {
        if (processedDataUrl && outName)
            downloadImage(processedDataUrl, outName);
    };

    const handleChangeImage = () => {
        clearObjectUrl();
        setFile(null);
        setImageSrc(null);
        setProcessedDataUrl(null);
        setOutName("");
        setQualityPercent(80);
    };

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Space
                direction="vertical"
                gap={40}
                style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    width: "100%",
                }}
            >
                <div>
                    <Title level={1}>Compress Image</Title>
                    <Text>Use the slider to pick your image quality</Text>
                </div>
                <Flex vertical align="center"></Flex>
                {!imageSrc && !processedDataUrl && (
                    <GeneralUploader
                        showUploadList={false}
                        beforeUpload={() => false}
                        accept="image/*"
                        multiple={false}
                        onChange={handleUploadChange}
                    />
                )}

                {imageSrc && !processedDataUrl && (
                    <Flex
                        vertical
                        gap={28}
                        align="center"
                        style={{ width: "100%" }}
                    >
                        <AntdImage
                            src={imageSrc}
                            alt="Uploaded"
                            preview={false}
                            style={{
                                maxHeight: 250,
                            }}
                        />

                        <Flex vertical style={{ width: 400 }} gap={12}>
                            <Text>Quality: {qualityPercent}%</Text>
                            <Slider
                                min={1}
                                max={100}
                                value={qualityPercent}
                                onChange={setQualityPercent}
                            />
                        </Flex>

                        <Space>
                            <Button
                                type="primary"
                                onClick={handleCompress}
                                loading={isProcessing}
                            >
                                Compress
                            </Button>
                            <Button
                                onClick={handleChangeImage}
                                disabled={isProcessing}
                            >
                                Change Image
                            </Button>
                        </Space>
                    </Flex>
                )}

                {processedDataUrl && (
                    <Card
                        size="small"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your image is ready
                        </Title>

                        <Flex vertical gap={14} style={{ marginTop: 20 }}>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleDownload}
                                style={{ minWidth: 220 }}
                            >
                                Download
                            </Button>
                            <Button
                                size="default"
                                onClick={handleChangeImage}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </Flex>
                    </Card>
                )}
            </Space>
        </div>
    );
}
